<template>
  <div :class="containerClass">
    <page-header
      title="View Skill"
      :container-class="null"
      :show-back="true"
      class="mb-3"
    />
    
    <b-alert show variant="light" class="transition-alert">
      <div class="d-flex align-items-center">
        <i class="material-icons text-primary mr-2">lock</i>
        <div>
          <span class="alert-heading">Transition Skill</span>
          <small class="d-block text-muted">This skill is managed by the Transition team and can't be edited.</small>
        </div>
      </div>
    </b-alert>

    <div v-if="isLoading" class="text-center my-5">
      <b-spinner class="align-middle mr-2"></b-spinner>
      <strong>Loading skill details...</strong>
    </div>

    <div v-else>
      <div class="info-block">
        <div class="info-header">
          <div class="thumb">
            <img v-if="skill.thumbnail_url" :src="skill.thumbnail_url" alt="Skill Thumbnail" />
            <div v-else class="thumb-placeholder">
              <i class="material-icons">play_circle</i>
            </div>
          </div>
          <div class="info-text">
            <h3 class="info-title">{{ skill.title }}</h3>
            <p class="info-meta" v-if="skill.program">{{ getProgramName(skill.program) }}</p>
            <div class="info-description" v-if="skill.description" v-safe-html="skill.description" />
            <p class="info-url" v-if="skill.video_url">
              Video URL:
              <a :href="skill.video_url" target="_blank" rel="noopener">{{ skill.video_url }}</a>
            </p>
            <p class="info-date" v-if="skill.created_at">Added on {{ formatDate(skill.created_at) }}</p>
          </div>
        </div>
      </div>

      <div class="info-block muted" v-if="skill.courses && skill.courses.length">
        <p class="section-label">
          <i class="material-icons align-middle mr-2">school</i>
          Associated courses
        </p>
        <p class="muted-text">This skill is shown inside the assigned LMS courses for your students.</p>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { mapActions } from 'vuex';
import Page from '../../../components/Page.vue';
import moment from 'moment';

export default {
  components: {
    PageHeader,
  },
  extends: Page,

  data() {
    return {
      title: 'View Skill',
      isLoading: true,
      skill: {
        title: '',
        description: '',
        video_url: '',
        thumbnail_url: '',
        program: null,
        courses: [],
        is_superuser_skill: true,
        created_at: null,
      },
      allPrograms: [],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Skills Library', to: { name: 'skills-library' } },
        {
          text: 'View Skill',
          active: true,
        },
      ];
    },
  },

  methods: {
    ...mapActions('skills', ['getSkillLibraryById']),
    ...mapActions('program', ['getAllPrograms']),

    async fetchSkillDetails() {
      this.isLoading = true;
      try {
        const skill_id = this.$route.params.id;
        const response = await this.getSkillLibraryById(skill_id);
        
        if (response.data) {
          this.skill = response.data;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load skill details' });
        this.$router.push({ name: 'skills-library' });
      }
      this.isLoading = false;
    },

    async fetchPrograms() {
      try {
        const response = await this.getAllPrograms({ ordering: 'id' });
        this.allPrograms = response.data || [];
      } catch (error) {
        this.allPrograms = [];
      }
    },

    getProgramName(programId) {
      const program = this.allPrograms.find(p => p.id === programId);
      return program ? program.title : '-';
    },

    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },

  async mounted() {
    await this.fetchPrograms();
    await this.fetchSkillDetails();
  },
};
</script>

<style scoped lang="scss">
// Subtle palette
$surface: #ffffff;
$surface-muted: #f5f6f8;
$text-main: #2f3a4a;
$text-muted: #6f7a85;
$border: #e3e7ee;
$accent: #2b71d9;

.transition-alert {
  border-radius: 12px;
  border: 1px solid $border;
  padding: 12px 16px;
  background: $surface;

  .alert-heading {
    font-weight: 600;
    color: $text-main;
  }
}

.info-block {
  background: $surface;
  border: 1px solid $border;
  border-radius: 14px;
  padding: 18px 22px;
  margin-bottom: 18px;
  box-shadow: 0 8px 18px -12px rgba(25, 42, 70, 0.25);

  &.muted {
    background: $surface-muted;
    border-style: dashed;
    border-color: lighten($border, 5%);
  }
}

.info-header {
  display: flex;
  gap: 18px;
  align-items: flex-start;
}

.thumb {
  width: 88px;
  height: 88px;
  border-radius: 16px;
  background: $surface-muted;
  border: 1px solid $border;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.thumb-placeholder i {
  font-size: 32px;
  color: $text-muted;
}

.info-text {
  flex: 1;
}

.info-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 600;
  color: $text-main;
}

.info-meta {
  margin: 0;
  color: $accent;
  font-weight: 500;
}

.info-description {
  margin-top: 8px;
  color: $text-muted;
  font-size: 14px;
  line-height: 1.5;
}

.info-url {
  margin-top: 10px;
  font-size: 14px;

  a {
    color: $accent;
    word-break: break-all;
  }
}

.info-date {
  margin: 10px 0 0;
  color: $text-muted;
  font-size: 13px;
}

.section-label {
  margin: 0 0 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
  color: $text-muted;
  font-weight: 600;
}

.muted-text {
  margin: 0;
  color: $text-muted;
  font-size: 14px;
}

@media (max-width: 575px) {
  .info-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .info-text {
    width: 100%;
  }

  .thumb {
    width: 80px;
    height: 80px;
  }
}
</style>
